// frontend/src/services/atozasSso.js
//
// Browser-side helpers for ATOZAS Cross-Domain SSO.
//
// All OIDC work happens on the backend. This module only:
//   - asks the backend whether SSO is switched on,
//   - starts login by navigating to the backend,
//   - restores the application JWT from an existing SSO session,
//   - ends the SSO session on logout.
//
// Every call uses `withCredentials` so the httpOnly SSO session cookie is sent.

import axios from 'axios';
import api from '../api';

// Human-readable text for the error codes the backend puts on ?atozas_error=.
const ERROR_MESSAGES = {
  access_denied: 'ATOZAS sign-in was cancelled.',
  invalid_session: 'Your ATOZAS sign-in session expired. Please try again.',
  login_expired: 'Your ATOZAS sign-in took too long. Please try again.',
  state_mismatch: 'ATOZAS sign-in could not be verified. Please try again.',
  missing_code: 'ATOZAS did not return an authorization code. Please try again.',
  invalid_token: 'ATOZAS returned an invalid token. Please try again.',
  email_required: 'Your ATOZAS account has no email address, which is required to sign in.',
  provider_unavailable: 'ATOZAS is currently unavailable. Please try again later.',
  session_error: 'Could not start ATOZAS sign-in. Please try again.',
  login_failed: 'ATOZAS sign-in failed. Please try again.',
};

export const getAtozasErrorMessage = (code) => (
  ERROR_MESSAGES[code] || 'ATOZAS sign-in failed. Please try again.'
);

/**
 * Reads the SSO feature flag from the backend.
 * Any failure is treated as "disabled" so the UI never shows a dead button.
 */
export const fetchAtozasConfig = async () => {
  try {
    const { data } = await axios.get(api.atozasConfig, {
      withCredentials: true,
      timeout: 8000,
    });

    return {
      enabled: Boolean(data && data.enabled),
      autoRedirect: Boolean(data && data.autoRedirect),
    };
  } catch (_error) {
    return { enabled: false, autoRedirect: false };
  }
};

/**
 * Starts the login flow with a full page navigation (an OIDC redirect cannot
 * happen over XHR). `returnTo` must be a local path; the backend re-validates it.
 */
export const startAtozasLogin = (returnTo = '/home') => {
  const safeReturnTo = typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//')
    ? returnTo
    : '/home';

  window.location.assign(`${api.atozasLogin}?returnTo=${encodeURIComponent(safeReturnTo)}`);
};

/**
 * Exchanges an existing ATOZAS session for this application's JWT.
 * Returns null when there is no SSO session (the normal case for OTP/Google users).
 */
export const restoreAtozasSession = async () => {
  try {
    const { data } = await axios.get(api.atozasMe, {
      withCredentials: true,
      timeout: 10000,
    });

    if (!data || !data.authenticated || !data.token) return null;
    return data;
  } catch (_error) {
    return null;
  }
};

/**
 * Ends the ATOZAS session server-side. Best effort: the caller clears local
 * state regardless of the outcome.
 */
export const endAtozasSession = async () => {
  try {
    const { data } = await axios.post(api.atozasLogout, {}, {
      withCredentials: true,
      timeout: 8000,
      // Non-simple header: forces a CORS preflight, which blocks cross-site CSRF.
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    return data || {};
  } catch (_error) {
    return {};
  }
};

/** Strips the SSO query flags so a refresh doesn't re-trigger the handshake. */
export const clearAtozasQueryParams = () => {
  try {
    const url = new URL(window.location.href);
    let changed = false;

    ['atozas', 'atozas_error'].forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });

    if (changed) {
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  } catch (_error) {
    // Non-fatal: leaving the params in the URL is harmless.
  }
};
